function isNull<T>(value: T | null): value is null {
  return value === null;
}

export class Maybe<T> {
  private constructor(private value: T | null) {}

  static some<T>(value: T) {
    return new Maybe<T>(value);
  }

  static none<T>() {
    return new Maybe<T>(null);
  }

  static fromValue<T>(value: T | null): Maybe<T> {
    return value === null ? Maybe.none<T>() : Maybe.some<T>(value);
  }

  static combine<T>(values: Maybe<T>[]): Maybe<T[]> {
    const raw = values.map(v => v.get());
    if (raw.some(isNull)) {
      return Maybe.none<T[]>();
    } else {
      return Maybe.some(raw as T[]);
    }
  }

  get(): T | null {
    return this.value;
  }

  getOrElse(defaultValue: T): T {
    return isNull(this.value) ? defaultValue : this.value;
  }

  map<U>(fn: (value: T) => U): Maybe<U> {
    if (isNull(this.value)) {
      return Maybe.none<U>();
    } else {
      return Maybe.fromValue<U>(fn(this.value));
    }
  }

  flatMap<U>(fn: (value: T) => Maybe<U>): Maybe<U> {
    if (isNull(this.value)) {
      return Maybe.none<U>();
    } else {
      return fn(this.value);
    }
  }

  tap(fn: (value: T) => void): this {
    if (this.value !== null) {
      fn(this.value);
    }
    return this;
  }
}
